import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import SemiCircle from '../../SemiCircle/components/SemiCircle';
import { toast, ToastContainer } from 'react-toastify';
import SlidingPane from "react-sliding-pane";
import Popup from "reactjs-popup";
import Select from 'react-select';

const axios = require('axios').default;

import ResendEmailSlidingState from './ResendEmailSlidingState';
import RefundModal from './RefundModal';
import RecentOrdersHeader from './RecentOrdersHeader';
import RecentOrdersTable from './RecentOrdersTable';
import { Mail01, Printer, ReverseLeft } from '@untitled-ui/icons-react';
import useTableColumns from '../../ReportingDashboard/hooks/useTableColumns';
import { orderColumnsFor, DEFAULT_ORDER_SORT, buildOrdersQuery } from '../constants/orderColumnsConfig';
import Tippy from '@tippyjs/react';
import styled from "styled-components";
import EventDetailsApp from '../../EventDetails/startup/EventDetailsApp';
import { offsetDateForBrowser } from '../../../shared/timeZoneLogic';

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  RadialBarChart,
  RadialBar,
} from "recharts";

import {
  TICKETS_SOLD_WIDGET_TAB,
  ADMISSION_WIDGET_TAB
} from '../constants/confirmDashboardConstants';

const formatDate = (date) => {
  return offsetDateForBrowser(date).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
};

const printTicketsContentStyle = {
  "backgroundColor": "var(--sl-bg-primary)",
  "textAlign": "left",
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "500px",
  "padding": "15px"
}

const CustomToolTipWrapper = styled.div`
  border: none;
  border-radius: 8px;
  background: black;
  padding: 13px;
  min-width: 100px;

  p {
    padding: 0;
    color: white;
    margin: 0;
  }

  .date {
    font-size: 12px;
    font-weight: 400;
    margin-bottom: 8px;
  }

  .value {
    font-size: 14px;
    font-weight: 700;
  }

  .label {
    font-size: 10px;
    font-weight: 400;
    margin-bottom: 8px;
  }
`;

const calculatePercentageSold = (confirm) => {
  var ticketsSold = confirm.number_of_manifested_tickets_sold;
  var venueCapacity = confirm.venue_capacity;

  if(venueCapacity === 0){
    return (ticketsSold > 0 ? 100 : 0);
  }

  return Math.ceil((ticketsSold / venueCapacity) * 100);
}

const calculatePercentageChecked = (confirm) => {
  var ticketsChecked = confirm.number_of_tickets_checked_in;
  var ticketsSold = confirm.number_of_manifested_tickets_sold;

  if(ticketsSold === 0){
    return (ticketsChecked > 0 ? 100 : 0);
  }

  return Math.ceil((ticketsChecked / ticketsSold) * 100);
}

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const manageTicketsUrl = (team, confirm) =>
  `/teams/${team.id}/confirms/${confirm.id}/ticketing?active_menu_item=TICKETS_MENU_ITEM`;
const manageAddOnsUrl = (team, confirm) =>
  `${manageTicketsUrl(team, confirm)}&active_ticketing_menu_item=TICKETING_ADD_ONS_MENU_ITEM`;
const seatingAssignmentsUrl = (team, confirm) =>
  `/teams/${team.id}/confirms/${confirm.id}/seating_assignments`;

const useFakeSalesData = (cumulativeSalesData) => {
  return cumulativeSalesData.length < 2;
}

const generateFakeSalesData = () => {
  var numberOfDays = Math.floor(Math.random() * 10) + 10;
  var summedAmount = Math.floor(Math.random() * 3000) + 150;
  var summedNumberOfTicketsSold = Math.floor(Math.random() * 50) + 10;

  return Array.apply(null, new Array(numberOfDays))
     .map(() => new Date())
     .map((v, i) => {
       v.setDate(v.getDate() - i);
       return v;
     })
     .reverse()
     .reduce((data, date) => {
       var newAmount = Math.floor(Math.random() * 3000) + 150;
       summedAmount  = (summedAmount + newAmount);
       var month = (date.getMonth() + 1);
       var day = date.getDate();
       var year = date.getFullYear() % 100;
       var numberOfTicketsSold = Math.floor(Math.random() * 50);
       summedNumberOfTicketsSold += numberOfTicketsSold;

       data.push({
         date: (month + "/" + day + "/" + year),
         amount: summedAmount,
         number_of_tickets_sold: summedNumberOfTicketsSold
       });

       return data;
     }, []);
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    var amount = Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(payload[0].payload.amount);
    var formattedNumberOfTicketsSold = Intl.NumberFormat().format(payload[0].payload.number_of_tickets_sold)

    return (
      <CustomToolTipWrapper>
        <p className="date">{ payload[0].payload.alt_date }</p>
        <p className="value">{ `${amount}` }</p>
        <p className="label">SALES</p>
        <p className="value">{ formattedNumberOfTicketsSold }</p>
        <p className="label">TICKETS SOLD</p>
      </CustomToolTipWrapper>
    );
  }

  return null;
};

const printViaServer = (csrfToken, team, objToPrint, setObjToPrint, selectedPrinter, setSelectedPrinter) => {
  axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

  const resource = `orders/${objToPrint.obj.id}`;

  axios.get(`/teams/${team.id}/${resource}/print?printer_id=${selectedPrinter.value}`)
    .catch(function (error) {
      toast.error("Could not print tickets. Plese try again.", {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: false,
        autoClose: 5000,
        hideProgressBar: true
      });
    })
    .finally(function () {
      setObjToPrint(null);
      setSelectedPrinter({ value: 1, label: "Printer 1" });
    });
}

const printLocal = (csrfToken, team, objToPrint, setObjToPrint) => {
  axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

  const resource = `orders/${objToPrint.obj.id}`;

  const pdfUrl = `/teams/${team.id}/${resource}/print_preview.pdf`;

  axios.get(pdfUrl, { responseType: 'blob' })
    .then(function (response) {
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      const existing = document.getElementById('local-print-iframe');
      if (existing) {
        document.body.removeChild(existing);
      }

      const iframe = document.createElement('iframe');
      iframe.id = 'local-print-iframe';
      iframe.style.position = 'fixed';
      iframe.style.top = '-10000px';
      iframe.style.left = '-10000px';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.src = url;

      iframe.onload = function () {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          window.open(url, '_blank');
        }

        setTimeout(() => {
          if (iframe.parentNode) {
            document.body.removeChild(iframe);
          }
          URL.revokeObjectURL(url);
        }, 60000);
      };

      document.body.appendChild(iframe);
    })
    .catch(function (error) {
      toast.error("Could not load tickets for printing. Please try again.", {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: false,
        autoClose: 5000,
        hideProgressBar: true
      });
    })
    .finally(function () {
      setObjToPrint(null);
    });
}

const print = (csrfToken, team, objToPrint, setObjToPrint, selectedPrinter, setSelectedPrinter, useLocalPrint) => {
  if (useLocalPrint) {
    printLocal(csrfToken, team, objToPrint, setObjToPrint);
  } else {
    printViaServer(csrfToken, team, objToPrint, setObjToPrint, selectedPrinter, setSelectedPrinter);
  }
}

const ConfirmDashboard = ({
  csrfToken,
  user,
  team,
  currentUser,
  confirm,
  confirmChanged,
  ticketTypes,
  addOns,
  recentOrderPaymentMethods,
  recentOrderSeatSections,
  typedSearchTerm,
  startSearchTermUpdate,
  searchedSearchTerm,
  csvDataSource,
  order,
  orderChanged,
  sendOrderReceipt,
  widgetTab,
  widgetTabChanged,
  addOnsByPrice,
  refund,
  fetchNewRefund,
  refundChanged,
  refundReasons,
  createRefund,
  submittingRefund,
  resendEmail,
  resendEmailChanged,
  sendingConfirmationEmail,
  cumulativeSalesData,
  fakeSalesData,
  fakeSalesDataChanged,
  confirmRefund,
  refundOrder,
  ticketBuyersData,
  fetchTicketBuyersData,
  promoters,
  buyers,
  ticketTypesByPrice,
  venueOwedBalance,
  canBypassRefundRestrictions,
  payoutInfo,
  salesData,
  hideHeader
}) => {
  useEffect(() => {
    if(refundOrder){
      fetchNewRefund(csrfToken, team, refundOrder);
    }

    fakeSalesDataChanged(generateFakeSalesData());
    fetchTicketBuyersData(csrfToken, team, confirm);
  }, []);

  const [selectedPrinter, setSelectedPrinter] = useState({ value: 1, label: "Printer 1"});
  const [objToPrint, setObjToPrint] = useState(null);
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [csvQuery, setCsvQuery] = useState(() => buildOrdersQuery({ sort: DEFAULT_ORDER_SORT, search: '' }));

  const orderColumns = useMemo(() => orderColumnsFor(confirm), [confirm.seatsio_event_id, confirm.add_on_seatsio_event_id]);
  const { visibleColumns, hiddenColumns, handleColumnsChange } = useTableColumns({
    columns: orderColumns,
    localStorageKey: (id) => `confirmDashboardOrderColumns_${id}`,
    teamId: team.id,
  });

  const resetColumnsToDefault = () => {
    const defaultVisible = orderColumns
      .filter((column) => !column.defaultHidden)
      .map((column) => ({ id: column.id, label: column.label, alwaysVisible: column.alwaysVisible }));
    const defaultHidden = orderColumns
      .filter((column) => column.defaultHidden)
      .map((column) => ({ id: column.id, label: column.label }));
    handleColumnsChange(defaultVisible, defaultHidden);
  };

  const fetchPrinters = (venueOwnershipId) => {
    setLoadingPrinters(true);
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    axios.get(`/teams/${team.id}/venue_ownerships/${venueOwnershipId}/printers`)
      .then(({ data }) => {
        setAvailablePrinters(data || []);

        if (data && data.length > 0) {
          const printerNumber = data[0].name.match(/printer-(\d+)/)?.[1] || 1;
          setSelectedPrinter({
            value: printerNumber,
            label: `Printer ${printerNumber}`
          });
        }
      })
      .catch((error) => {
        setAvailablePrinters([]);
      })
      .finally(() => {
        setLoadingPrinters(false);
      });
  };

  const fetchOrder = (csrfToken, team, orderId) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    axios.get(`/teams/${team.id}/orders/${orderId}.json`)
      .then(({ data }) => {
        orderChanged(data);
      })
      .catch((error) => {
        toast.error("Could not get order data. Plese try again.", {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  }

  const orderMenuItems = (rowOrder) => [
    ...(team.package.printing && confirm.venue_ownership.can_print ? [{
      label: 'Print Tickets',
      icon: Printer,
      onClick: () => {
        if (!confirm.venue_ownership.use_local_print) {
          fetchPrinters(confirm.venue_ownership.id);
        }
        setObjToPrint({ type: "order", obj: rowOrder });
      }
    }] : []),
    { label: 'Refund Order', icon: ReverseLeft, onClick: () => fetchNewRefund(csrfToken, team, rowOrder) },
    ...(rowOrder.email ? [{
      label: 'Resend Order Confirmation',
      icon: Mail01,
      onClick: () => fetchOrder(csrfToken, team, rowOrder.id)
    }] : [])
  ];

  const deliveryStatusStyles = (status) => {
    if(status === "Immediate") {
      return {
        color: "var(--sl-text-brand)",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "var(--sl-border-brand)",
        backgroundColor: "var(--sl-bg-brand-primary)",
      };
    } else if(status === "Scheduled") {
      return {
        color: "var(--sl-text-warning)",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "var(--sl-border-secondary)",
        backgroundColor: "var(--sl-bg-warning-primary)",
      };
    } else if(status === "Delivered") {
      return {
        color: "var(--sl-text-success)",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "var(--sl-border-secondary)",
        backgroundColor: "var(--sl-bg-success-primary)",
      };
    }
  }

  return (
    <div className="confirm-dashboard">
      <ToastContainer icon={false} />
      {!hideHeader && (
      <div className="row m-0">
        <div className="col-12">
          <div className="page-title d-flex align-items-center">
            { confirm.name }
            {confirm.calendar_classification === "past" ? (
              <span className="badge badge-secondary mt-1 ml-3 p-2"
                    style={{"background": "#919191", "color": "white", "fontSize": "10px"}}>
                Past
              </span>
            ) : confirm.calendar_classification === "canceled" ? (
              <span className="badge badge-danger mt-1 ml-3 p-2"
                    style={{"fontSize": "10px"}}>
                Canceled
              </span>
            ) : confirm.calendar_classification === "postponed" ? (
              <span className="badge badge-warning mt-1 ml-3 p-2"
                    style={{"fontSize": "10px"}}>
                Postponed
              </span>
            ) : confirm.calendar_classification === "on_sale" ? (
              <span className="badge badge-secondary mt-1 ml-3 p-2"
                    style={{"background": "#5f5383", "color": "white", "fontSize": "10px"}}>
                On Sale
              </span>
            ) : confirm.calendar_classification === "pre_sale" ? (
              <span className="badge badge-secondary mt-1 ml-3 p-2"
                    style={{"background": "#FF7A00", "color": "white", "fontSize": "10px"}}>
                Pre Sale
              </span>
            ) : confirm.calendar_classification === "announced" ? (
              <span className="badge badge-secondary mt-1 ml-3 p-2"
                    style={{"background": "#98c648", "color": "white", "fontSize": "10px"}}>
                Announced
              </span>             
            ) :  (
              <span className="badge badge-secondary mt-1 ml-3 p-2"
                    style={{"background": "#4c81b9", "color": "white", "fontSize": "10px"}}>
                Confirmed
              </span>
            )}
          </div>
          <EventDetailsApp
            csrfToken={csrfToken}
            team={team}
            currentUser={currentUser}
            confirm={{...confirm}}
            confirmSaved={(savedConfirm) => {
              confirmChanged(savedConfirm);
            }}
            buyers={[...buyers]}
            promoters={promoters} />
        </div>
      </div>
      )}
      <div className="row mb-4 mx-0">
        <div className="col-12">
          <div className="card border-0 shadow-2">
            <div className="card-body"
                 style={{"padding": "24px"}}>
              <div className="row">
                <div className="col">
                  <h3 className="mb-5"
                      style={{"fontSize": "24px"}}>
                    <strong>
                      Ticket Dashboard
                    </strong>
                  </h3>
                </div>
                <div className="col-xs-auto d-flex align-items-center"
                     style={{"gap": "8px", "paddingRight": "15px"}}>
                  <a href={manageTicketsUrl(team, confirm)}
                     className="btn btn-external">
                    <strong>Manage Tickets</strong>
                  </a>
                  <a href={manageAddOnsUrl(team, confirm)}
                     className="btn btn-external">
                    <strong>Manage Add-ons</strong>
                  </a>
                </div>
                {confirm.calendar_classification === "on_sale" ? (
                  <div className="col-xs-auto"
                       style={{"paddingRight": "15px"}}>
                    <div className="rounded"
                         style={{'background': "var(--sl-bg-active)", "padding": "16px"}}>
                      <div className="row">
                        <div className="col-xs-auto d-flex align-items-center"
                             style={{"paddingLeft": "15px"}}>
                          <i className="far fa-ticket-alt fa-rotate-90"
                             style={{"color": "#98c648", "fontSize": "24px"}}></i>
                        </div>
                        <div className="col"
                             style={{"paddingLeft": "9px"}}>
                          <p className="mb-0 text-dark"
                             style={{"fontSize": "16px"}}>
                            <strong>On Sale</strong>
                          </p>
                          <p className="mb-0 text-dark small"
                             style={{"marginTop": "-5px"}}>
                            This event is live and on sale.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className='row bordered-cols'
                   style={{
                     "marginTop": "20px",
                     "marginBottom": "25px"
                   }}>
                <div className="col-12 col-lg-8 right-border-for-lg-and-up">
                  <p className="mb-0" style={{"fontSize": "20px"}}>
                    <strong>Sales</strong>
                  </p>
                  <div className="row">
                    <div className="col-12 col-sm-auto mb-5 mb-lg-0"
                         style={{"paddingLeft": "15px", "paddingRight": "25px"}}>
                      <p className="mb-0 mt-4 text-muted">
                        <strong>
                          Venue Gross
                        </strong>
                      </p>
                      <p className="mb-0" style={{"fontSize": "24px"}}>
                        <strong>
                          {formatter.format(parseFloat(salesData.gross))}
                        </strong>
                      </p>
                      <p className="mb-0 mt-1 text-muted small">
                        <strong>
                          Tickets Gross
                        </strong>
                      </p>
                      <p className="mb-0" style={{"fontSize": "16px", "marginTop": "-4px"}}>
                        <strong>
                          {formatter.format(parseFloat(salesData.tickets))}
                        </strong>
                      </p>
                      <p className="mb-0 mt-1 text-muted small">
                        <strong>
                          Add-Ons Gross
                        </strong>
                      </p>
                      <p className="mb-0" style={{"fontSize": "16px", "marginTop": "-4px"}}>
                        <strong>
                          {formatter.format(parseFloat(salesData.add_ons))}
                        </strong>
                      </p>
                      {parseFloat(salesData.other) > 0.00 ? (
                        <React.Fragment>
                          <p className="mb-0 mt-1 text-muted small">
                            <strong>
                              Other Gross
                            </strong>
                            <Tippy placement={'top'}
                                    content={
                                      <div style={{
                                            "fontSize": "12px"
                                          }}>
                                        {formatter.format(parseFloat(salesData.donations))} Donations + {formatter.format(parseFloat(salesData.require_per_ticket_donations))} Custom Fees & Surcharges
                                      </div>
                                    }
                                    maxWidth={200}
                                    animation={'shift-away'}
                                    theme='material'>
                              <i className="fas fa-info-circle ml-1"></i>
                            </Tippy>
                          </p>
                          <p className="mb-0" style={{"fontSize": "16px", "marginTop": "-4px"}}>
                            <strong>
                              {formatter.format(parseFloat(salesData.other))}
                            </strong>
                          </p>
                        </React.Fragment>
                      ) : null}
                      <p className="mb-0 mt-1 text-muted small">
                        <strong>
                          Royalties
                        </strong>
                      </p>
                      <p className="mb-0" style={{"fontSize": "16px", "marginTop": "-4px"}}>
                        <strong>
                          {formatter.format(parseFloat(salesData.royalties))}
                        </strong>
                      </p>
                      {parseFloat(salesData.taxes) > 0.00 ? (
                        <React.Fragment>
                          <p className="mb-0 mt-1 text-muted small">
                            <strong>
                              Taxes
                            </strong>
                          </p>
                          <p className="mb-0" style={{"fontSize": "16px", "marginTop": "-4px"}}>
                            <strong>
                              {formatter.format(parseFloat(salesData.taxes))}
                            </strong>
                          </p>
                        </React.Fragment>
                      ) : null}
                      {parseFloat(salesData.refunds) > 0.00 ? (
                        <React.Fragment>
                          <p className="mb-0 mt-1 text-muted small">
                            <strong>
                              Refunded
                            </strong>
                          </p>
                          <p className="mb-0" style={{"fontSize": "16px", "marginTop": "-4px"}}>
                            <strong>
                              ({formatter.format(parseFloat(salesData.refunds))})
                            </strong>
                          </p>
                        </React.Fragment>
                      ) : null}
                      {parseFloat(salesData.disputes_lost) > 0.00 ? (
                        <React.Fragment>
                          <p className="mb-0 mt-1 text-muted small">
                            <strong>
                              Disputes Lost
                            </strong>
                          </p>
                          <p className="mb-0" style={{"fontSize": "16px", "marginTop": "-4px"}}>
                            <strong>
                              ({formatter.format(parseFloat(salesData.disputes_lost))})
                            </strong>
                          </p>
                        </React.Fragment>
                      ) : null}
                      {parseFloat(salesData.disputes_won) > 0.00 ? (
                        <React.Fragment>
                          <p className="mb-0 mt-1 text-muted small">
                            <strong>
                              Disputes Won
                            </strong>
                          </p>
                          <p className="mb-0" style={{"fontSize": "16px", "marginTop": "-4px"}}>
                            <strong>
                              {formatter.format(parseFloat(salesData.disputes_won))}
                            </strong>
                          </p>
                        </React.Fragment>
                      ) : null}
                      {parseFloat(salesData.adjustments) !== 0.00 ? (
                        <React.Fragment>
                          <p className="mb-0 mt-1 text-muted small">
                            <strong>
                              Adjustments
                            </strong>
                          </p>
                          <p className="mb-0" style={{"fontSize": "16px", "marginTop": "-4px"}}>
                            <strong>
                              {formatter.format(parseFloat(salesData.adjustments))}
                            </strong>
                          </p>
                        </React.Fragment>
                      ) : null}
                      <p className="mb-0 mt-1 text-muted small">
                        <strong>
                          Net
                        </strong>
                      </p>
                      <p className="mb-0" style={{"fontSize": "16px", "marginTop": "-4px"}}>
                        <strong>
                          {formatter.format(parseFloat(salesData.net))}
                        </strong>
                      </p>
                    </div>
                    <div className="col mb-5 mb-lg-0">
                      {/* https://github.com/recharts/recharts/issues/172#issuecomment-992240256 */}
                      <div className={"net-sales-chart mt-4 " + (useFakeSalesData(cumulativeSalesData) ? "placeholder" : "")}>
                        <div style={{
                              width: "100%",
                              height: "235px",
                              overflow: "hidden",
                              position: "relative",
                            }}>
                          <div style={{
                                 width: "100%",
                                 height: "100%",
                                 position: "absolute",
                                 top: 0,
                                 left: 0,
                               }}>
                            {!useFakeSalesData(cumulativeSalesData) ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <AreaChart
                                  data={cumulativeSalesData}
                                  margin={{
                                    top: 10,
                                    right: 30,
                                    left: 0,
                                    bottom: 0
                                  }}
                                >
                                  <Tooltip content={<CustomTooltip />} />
                                  <CartesianGrid vertical={false}
                                                 stroke="var(--sl-border-secondary)"
                                                 strokeDasharray="3 3" />
                                  <XAxis dataKey="date"
                                         tickLine={false}
                                         axisLine={false} />
                                  <YAxis axisLine={false}
                                         tickFormatter={
                                           (value) => {
                                             var compact = Intl.NumberFormat('en-US', {
                                               notation: "compact",
                                               maximumFractionDigits: 1
                                             }).format(value);

                                             return ("$" + compact);
                                           }
                                         }
                                         tickLine={false} />
                                  <Area type="linear"
                                        dataKey="amount"
                                        stroke="#6A4C93"
                                        strokeWidth={2}
                                        fillOpacity={1}
                                        fill="rgba(106, 76, 147, 0.3)" />
                                </AreaChart>
                              </ResponsiveContainer>
                            ) : (
                              <ResponsiveContainer width="100%" height="100%">
                                <AreaChart
                                  data={fakeSalesData}
                                  margin={{
                                    top: 10,
                                    right: 30,
                                    left: 0,
                                    bottom: 0
                                  }}
                                >
                                  <Tooltip content={<CustomTooltip />} />
                                  <CartesianGrid vertical={false}
                                                 stroke="var(--sl-border-secondary)"
                                                 strokeDasharray="3 3" />
                                  <XAxis dataKey="date"
                                         tickLine={false}
                                         axisLine={false} />
                                  <YAxis axisLine={false}
                                         tickFormatter={
                                           (value) => {
                                             var compact = Intl.NumberFormat('en-US', {
                                               notation: "compact",
                                               maximumFractionDigits: 1
                                             }).format(value);

                                             return ("$" + compact);
                                           }
                                         }
                                         tickLine={false} />
                                  <Area type="linear"
                                        dataKey="amount"
                                        stroke="var(--sl-border-secondary)"
                                        strokeWidth={2}
                                        fillOpacity={1}
                                        fill="var(--sl-bg-secondary)" />
                                </AreaChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                          {useFakeSalesData(cumulativeSalesData) ? (
                            <div className="position-absolute"
                                 style={{
                                   "left": "50%",
                                   "top": "40%"
                                 }}>
                              <div style={{
                                     "position": "relative",
                                     "left": "-50%",
                                     "top": "-40%"
                                   }}>
                                <p className="mb-0 text-dark"
                                   style={{
                                     "fontSize": "16px"
                                   }}>
                                  <strong>Not enough data yet</strong>
                                </p>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-lg-4 extra-left-paddin-lg-and-up">
                  <ul className="nav nav-tabs">
                    <li className="nav-item">
                      <a href="#"
                         onClick={
                           (e) => {
                             e.preventDefault();
                             widgetTabChanged(TICKETS_SOLD_WIDGET_TAB);
                           }
                         }
                         className={"nav-link px-4 " + (widgetTab === TICKETS_SOLD_WIDGET_TAB ? "active" : "")}>
                        <strong>Tickets Sold</strong>
                      </a>
                    </li>
                    <li className="nav-item">
                      <a href="#"
                         onClick={
                           (e) => {
                             e.preventDefault();
                             widgetTabChanged(ADMISSION_WIDGET_TAB);
                           }
                         }
                         className={"nav-link px-4 " + (widgetTab === ADMISSION_WIDGET_TAB ? "active" : "")}>
                        <strong>Admission</strong>
                      </a>
                    </li>
                  </ul>
                  {widgetTab === TICKETS_SOLD_WIDGET_TAB ? (
                    <div>
                      <div className="row px-2"
                           style={{"paddingTop": "38px"}}>
                        <div className="col-12 col-lg-4 d-flex justify-content-between flex-row flex-lg-column">
                          <dl>
                            <dt className="text-muted">
                              <strong className="text-no-wrap">Sold</strong>
                            </dt>
                            <dd style={{"marginTop": "-5px"}}>
                              <strong className="text-no-wrap"
                                      style={{"fontSize": "24px"}}>
                                {confirm.number_of_manifested_tickets_sold}
                              </strong>
                            </dd>
                          </dl>
                          <dl>
                            <dt className="text-muted">
                              <strong className="text-no-wrap">Capacity</strong>
                            </dt>
                            <dd style={{"marginTop": "-5px"}}>
                              <strong className="text-no-wrap"
                                      style={{"fontSize": "24px"}}>
                                {confirm.venue_capacity}
                              </strong>
                            </dd>
                          </dl>
                        </div>
                        <div className="col-12 col-lg-8 d-flex justify-content-center"
                             style={{"paddingBottom": "35px"}}>
                          <SemiCircle wrapperClassName="align-self-end"
                                      percentage={calculatePercentageSold(confirm)} />
                        </div>
                      </div>

                      {currentUser.can_see_unmanifested_tickets ? (
                        <div className="row">
                          <div className="col-12">
                            <div className="card border-0 rounded-0"
                                 style={{
                                   "background": "var(--sl-bg-secondary)",
                                   "marginBottom": "11px"
                                 }}>
                              <div className="card-body text-center m-0 py-3">
                                <span style={{"fontSize": "16px"}}><strong>{confirm.number_of_tickets_issued} </strong></span>
                                <span className="text-muted small">
                                  <strong>
                                    Tickets Issued
                                  </strong>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="row">
                          <div className="col-12 mb-3">
                          </div>
                        </div>
                      )}

                    </div>
                  ) : null}
                  {widgetTab === ADMISSION_WIDGET_TAB ? (
                    <div>
                      <div className="row px-2"
                           style={{"paddingTop": "38px"}}>
                        <div className="col-12 col-lg-4 d-flex justify-content-between flex-row flex-lg-column">
                          <dl>
                            <dt className="text-muted">
                              <strong className="text-no-wrap">Checked</strong>
                            </dt>
                            <dd style={{"marginTop": "-5px"}}>
                              <strong className="text-no-wrap"
                                      style={{"fontSize": "24px"}}>
                                {confirm.number_of_tickets_checked_in}
                              </strong>
                            </dd>
                          </dl>
                          <dl>
                            <dt className="text-muted">
                              <strong className="text-no-wrap">Sold</strong>
                            </dt>
                            <dd style={{"marginTop": "-5px"}}>
                              <strong className="text-no-wrap"
                                      style={{"fontSize": "24px"}}>
                                {confirm.number_of_manifested_tickets_sold}
                              </strong>
                            </dd>
                          </dl>
                        </div>
                        <div className="col-12 col-lg-8 d-flex justify-content-center"
                             style={{"paddingBottom": "25px"}}>
                          <SemiCircle wrapperClassName="align-self-end"
                                      percentage={calculatePercentageChecked(confirm)} />
                        </div>
                        <div className="col-12">
                          <div className="table-responsive">
                          <table className="table mb-0 vertical-middle table-sm responsive-card-table"
                                 style={{tableLayout: "fixed"}}>
                            <colgroup>
                              <col span={1} style={{"width": "75%"}} />
                              <col span={1} style={{"width": "25%"}} />
                            </colgroup>
                            <tbody>
                              {ticketTypes.map((ticketType, index) =>
                                <tr className="border-0" key={index}>
                                  <td className="overflow-ellipsis text-muted border-0 px-0" data-label="Ticket Type">
                                    <strong title={ticketType.name}>
                                      {ticketType.name}
                                    </strong>
                                  </td>
                                  <td className="px-0 border-0 text-right" data-label="Checked In / Sold">
                                    <strong style={{"fontSize": "16px"}}>
                                      {ticketType.number_of_tickets_checked_in}/{ticketType.number_of_tickets_sold}
                                    </strong>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              {payoutInfo.payoutBalanceTransactions.length > 0 ? (
                <div style={{"marginTop": "20px", "marginBottom": "25px"}}>
                  <div className='row mb-1'>
                    <div className='col-12 col-lg-8'>
                      <div className='d-flex justify-content-between align-items-center'>
                        <p className="mb-0" style={{"fontSize": "20px"}}>
                          <strong>Payouts</strong>
                        </p>
                        <a href={`/teams/${team.id}/payouts`}>
                          <strong>
                            See all payouts
                            <i className="fas fa-angle-right ml-1"></i>
                          </strong>
                        </a>
                      </div>
                    </div>
                    <div className='col-12 col-lg-4'>
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-12 col-lg-8'>
                      <div className="table-responsive">
                      <table className='table vertical-middle table-bordered-bottom no-top-border responsive-card-table'>
                        <colgroup>
                          <col span={1} style={{"width": "25%"}} />
                          <col span={1} style={{"width": "50%"}} />
                          <col span={1} style={{"width": "25%"}} />
                        </colgroup>
                        <tbody>
                          {payoutInfo.payoutBalanceTransactions.map((balanceTransaction, index) =>
                            <tr key={index}>
                              <td data-label="Date">
                                {balanceTransaction.human_created_at}
                              </td>
                              <td className="overflow-ellipsis text-center" data-label="Funding Source">
                                {balanceTransaction.dwolla_funding_source ? (
                                  <span title={balanceTransaction.dwolla_funding_source.name_with_mask}>
                                    {balanceTransaction.dwolla_funding_source.name_with_mask}
                                  </span>
                                ) : (
                                  <span>ACCOUNT ON FILE</span>
                                )}
                              </td>
                              <td className='text-right' data-label="Amount">
                                {balanceTransaction.category === "debit" ? (
                                  <span>{formatter.format(parseFloat(balanceTransaction.amount))}</span>
                                ) : (
                                  <span>-{formatter.format(parseFloat(balanceTransaction.amount))}</span>
                                )}
                                <a href={`/teams/${team.id}/balance_transactions/${balanceTransaction.id}.csv`} className='text-muted ml-2'>
                                  <i className="fas fa-download"></i>
                                </a>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      </div>
                    </div>
                    <div className='col-12 col-lg-4'>
                      <div className='card border-0' style={{"background": "var(--sl-bg-active)"}}>
                        <div className='card-body'>
                          <p className='text-muted mb-0'>
                            <strong>Paid Out</strong>
                          </p>
                          <p className='text-dark mb-3' style={{"fontSize": "24px", "marginTop": "-5px"}}>
                            <strong>{formatter.format(parseFloat(payoutInfo.paidOutAmount))}</strong>
                          </p>
                          <p className='text-muted mb-0'>
                            <strong>Payout Remaining</strong>
                          </p>
                          <p className='text-dark mb-3' style={{"fontSize": "24px", "marginTop": "-5px"}}>
                            <strong>{formatter.format(parseFloat(payoutInfo.payoutRemaining))}</strong>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
              <div
                className='row bordered-cols'
                style={{
                  "marginTop": "20px",
                  "marginBottom": "25px"
                }}
              >
                <div className="col-12">
                  <p className="mb-0" style={{"fontSize": "20px"}}>
                    <strong>Ticket Buyers</strong>
                  </p>
                </div>
                { ticketBuyersData.length == 0 ? (
                  <div className="col-12 text-center">
                    <img src="/uploading-loading.gif"
                         style={{width: "50px"}} />
                  </div>
                ) : (
                  <div className="col-12 row">
                    <div
                      className="col-12 col-lg-3 d-none d-md-block"
                      style={{ "minHeight": "150px" }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <RadialBarChart
                          cx="50%"
                          cy="50%"
                          innerRadius="10%"
                          outerRadius="100%"
                          barSize={20}
                          data={Object.assign([], ticketBuyersData).reverse()}
                        >
                          <RadialBar
                            minAngle={15}
                            background
                            clockWise
                            dataKey="count"
                          />
                        </RadialBarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="col-12 col-lg-9">
                      <div className="row">
                        { ticketBuyersData.map((ticketBuyerData, index) =>
                          <div className="col-6 col-md-4 mb-3" key={index}>
                            <div className="row">
                              <div className="col-12 d-flex align-items-center">
                                <div
                                  style={{
                                    "width": "10px",
                                    "height": "10px",
                                    "backgroundColor": `${ticketBuyerData.fill}`,
                                    "marginRight": "5px",
                                    "borderRadius": "2px",
                                    "flexShrink": "0"
                                  }}
                                >
                                </div>
                                <div style={{"fontSize": "14px"}}>
                                  { ticketBuyerData.name }
                                </div>
                                <Tippy
                                  content={
                                    <div style={{
                                          "fontSize": "12px"
                                        }}>
                                      { ticketBuyerData.tooltip }
                                    </div>
                                  }
                                  placement={'top'}
                                  maxWidth={200}
                                  animation={'shift-away'}
                                  theme='material'
                                >
                                  <i className="fas fa-info-circle text-muted ml-2"></i>
                                </Tippy>
                              </div>
                              <div className="col-12">
                                <h2
                                  className="font-700 ticket-buyers-count"
                                  style={{ "fontSize": "34px" }}
                                >
                                  { Intl.NumberFormat().format(ticketBuyerData.count) }
                                </h2>
                              </div>
                              { (ticketBuyerData.total_description != undefined) && (
                                <div
                                  className="col-12"
                                  style={{
                                    "fontSize": "13px",
                                    "color": "var(--sl-text-secondary)",
                                  }}
                                >
                                    { ticketBuyerData.total_description }
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className='row'>
                <div className="mb-4 mb-lg-0 col-12 col-lg-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <p className="mb-0" style={{"fontSize": "20px"}}>
                        <strong>Delivery</strong>
                      </p>
                        <span
                          className="mt-1 ml-2 px-2 small rounded"
                          style={deliveryStatusStyles(confirm.delivery_status)}
                        >
                        {confirm.delivery_status}
                      </span>
                    </div>
                    <div style={{fontSize: "16px"}}>
                      <i className="fas fa-ellipsis-v text-muted"></i>
                    </div>
                  </div>
                  <div style={{borderBottom: "2px solid var(--sl-border-secondary)"}}>
                    <p className="my-1 text-muted small">
                      {["Scheduled", "Delivered"].includes(confirm.delivery_status) ? formatDate(confirm.delivery_time) : null}
                    </p>
                  </div>
                  {confirm.delivery_status === "Scheduled" ? (
                    <>
                      <div
                        className="mt-3 mt-lg-5 text-center"
                        style={{fontSize: "24px"}}
                      >
                        <strong>
                          {confirm.delivery_time_relative}
                        </strong>
                      </div>
                      <div className="mt-1 mt-lg-2 text-center">
                        <span>until delivery via email</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="mt-3">
                        Orders sent
                      </p>
                      <div
                        className="mt-3"
                        style={{fontSize: "24px"}}
                      >
                        <strong>{confirm.email_deliveries}</strong>
                      </div>
                    </>
                  )}
                </div>
                <div className="col-12 col-lg-9">
                  <div className="row">
                    <div className="col">
                      <p className="mb-0" style={{"fontSize": "20px"}}>
                        <strong>Sales by Ticket Type</strong>
                      </p>
                    </div>
                    <div className="col-xs-auto"
                         style={{"paddingRight": "15px"}}>
                      <a href={manageTicketsUrl(team, confirm)}
                         className="btn btn-external btn-sm">
                        <strong>Manage Tickets</strong>
                      </a>
                    </div>
                  </div>
                  {ticketTypesByPrice.length === 0 ? (
                    <div className="h-100 d-flex align-items-center justify-content-center">
                      <p>
                        No Sales Yet
                      </p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                    <table className="table mt-2 mb-0 vertical-middle table-bordered-bottom responsive-card-table"
                           style={{tableLayout: "fixed"}}>
                      <colgroup>
                        <col span={1} style={{"width": "70%"}} />
                        <col span={1} style={{"width": "15%"}} />
                        <col span={1} style={{"width": "15%"}} />
                      </colgroup>
                      <tbody>
                        {ticketTypesByPrice.map((salesData, index) =>
                          <tr key={index}
                              className={"border-top-0 " + (salesData.ticket_type.scheduled ? "text-muted" : "text-dark")}>
                            <td className="overflow-ellipsis pl-0" data-label="Product">
                              <div>
                                <strong title={salesData.product_name}>
                                  {salesData.product_name}
                                </strong>
                                {!salesData.ticket_type.manifested ? (
                                  <span className="ml-2 text-muted">
                                    (unmanifested)
                                  </span>
                                ) : null}
                              </div>
                              {salesData.ticket_type.scheduled ? (
                                <div className="small">
                                  Scheduled
                                </div>
                              ) : null}
                            </td>
                            <td className="text-center pl-0" data-label="Qty / Available">
                              {salesData.quantity}/{salesData.ticket_type.available}
                            </td>
                            <td className="text-center pl-0" data-label="Price">
                              {parseFloat(salesData.price) === 0 ? (
                                "FREE"
                              ) : (
                                formatter.format(salesData.price)
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    </div>
                  )}
                </div>
              </div>
              {addOnsByPrice.length > 0 ? (
                <div className='row'>
                  <div className="col-12"
                       style={{"marginTop": "53px"}}>
                    <div className="row">
                      <div className="col">
                        <p className="mb-0" style={{"fontSize": "20px"}}>
                          <strong>Sales by Add-on</strong>
                        </p>
                      </div>
                      <div className="col-xs-auto pr-2">
                        <a href={seatingAssignmentsUrl(team, confirm)}
                           className="btn btn-external btn-sm">
                          <strong>Seating Assignments</strong>
                        </a>
                      </div>
                      <div className="col-xs-auto"
                           style={{"paddingRight": "15px"}}>
                        <a href={manageAddOnsUrl(team, confirm)}
                           className="btn btn-external btn-sm">
                          <strong>Manage Add-ons</strong>
                        </a>
                      </div>
                    </div>
                    <div className="table-responsive">
                    <table className="table mt-2 mb-0 vertical-middle table-bordered-bottom responsive-card-table"
                           style={{tableLayout: "fixed"}}>
                      <colgroup>
                        <col span={1} style={{"width": "70%"}} />
                        <col span={1} style={{"width": "15%"}} />
                        <col span={1} style={{"width": "15%"}} />
                      </colgroup>
                      <tbody>
                        {addOnsByPrice.sort((a, b) => a.add_on.position - b.add_on.position).map((salesData, index) =>
                          <tr key={index}
                              className={"border-top-0 " + (salesData.add_on.scheduled ? "text-muted" : "text-dark")}>
                            <td className="overflow-ellipsis pl-0" data-label="Add-on">
                              <strong title={salesData.product_name}>
                                {salesData.product_name}
                              </strong>
                              {salesData.add_on.scheduled ? (
                                <div className="small">
                                  Scheduled
                                </div>
                              ) : null}
                            </td>
                            <td className="text-center pl-0" data-label="Qty / Available">
                              {salesData.quantity}/{salesData.add_on.available}
                            </td>
                            <td className="text-center pl-0" data-label="Price">
                              {formatter.format(salesData.price)}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    </div>
                  </div>
                </div>
              ) : null}
              <Popup
                open={objToPrint != null}
                arrow={false}
                modal={true}
                contentStyle={printTicketsContentStyle}
              >
                {close => (
                  <div className="">
                    <div className="row">
                      <div className="col">
                        <div className="row">
                          <div className="col">
                            <h5 style={{"marginBottom": "16px"}}>
                              <div className="strong">
                                Print tickets?
                              </div>
                            </h5>
                            {!confirm.venue_ownership.use_local_print && loadingPrinters && (
                              <p className="text-muted">Loading printers...</p>
                            )}
                          </div>
                          <div className="col-xs-auto">
                            <a href="#"
                               className="text-dark"
                               style={{"paddingRight": "15px"}}
                               onClick={e => {
                                 e.preventDefault();
                                 setObjToPrint(null);
                                 setAvailablePrinters([]);
                               }} >
                              <i className="fa fa-times"></i>
                            </a>
                          </div>
                        </div>
                        {confirm.venue_ownership.use_local_print ? (
                          <div className="row">
                            <div className="col">
                              <p className="text-muted mb-0">Printing to your local printer</p>
                            </div>
                          </div>
                        ) : availablePrinters.length > 1 ? (
                          <div className="row">
                            <div className="col">
                              <strong>Select a printer</strong>
                              <Select
                                value={selectedPrinter}
                                options={availablePrinters.map((printer, index) => {
                                  const printerNumber = printer.name.match(/printer-(\d+)/)?.[1] || (index + 1);
                                  return {
                                    value: printerNumber,
                                    label: `Printer ${printerNumber}`
                                  };
                                })}
                                onChange={option => setSelectedPrinter(option)}
                                isDisabled={loadingPrinters}
                              />
                            </div>
                          </div>
                        ) : availablePrinters.length === 1 ? (
                          <div className="row">
                            <div className="col">
                              <p className="text-muted mb-0">Printer 1 selected</p>
                            </div>
                          </div>
                        ) : !loadingPrinters ? (
                          <div className="row">
                            <div className="col">
                              <p className="text-danger mb-0">No printers available</p>
                            </div>
                          </div>
                        ) : null}
                        {!confirm.venue_ownership.use_local_print && (
                          <div className="row mb-3">
                            {user.admin === true ? (
                              <div className="col">
                                <a href={`/teams/${team.id}/venue_ownerships`}
                                   target="_blank"
                                   rel="noopener noreferrer"
                                   className="text-primary small"
                                   style={{"fontSize": "12px"}}>
                                  Manage printers
                                </a>
                              </div>
                            ) : null}
                          </div>
                        )}
                        <p>The tickets for this order will be printed.</p>
                        <p>This cannot be undone.</p>
                        <p>Are you sure?</p>
                        <div className="text-right">
                          <a
                            href="#"
                            onClick={e => {
                              e.preventDefault();
                              setObjToPrint(null);
                            }}>
                            Cancel
                          </a>
                          <a
                            href="#"
                            className={`btn btn-danger ml-3 ${(!confirm.venue_ownership.use_local_print && (loadingPrinters || availablePrinters.length === 0)) ? 'disabled' : ''}`}
                            onClick={e => {
                              e.preventDefault();
                              if (!confirm.venue_ownership.use_local_print && availablePrinters.length === 0) return;

                              print(csrfToken, team, objToPrint, setObjToPrint, selectedPrinter, setSelectedPrinter, confirm.venue_ownership.use_local_print);
                            }}>

                            Print Tickets
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Popup>
              <div className='row'>
                <div className="col-12"
                     style={{"marginTop": "53px"}}>
                  <div className="recent-orders-table">
                    <RecentOrdersHeader csvDataSource={csvDataSource}
                                        csvQuery={csvQuery}
                                        team={team}
                                        confirm={confirm}
                                        currentUser={currentUser}
                                        visibleColumns={visibleColumns}
                                        hiddenColumns={hiddenColumns}
                                        onColumnsChange={handleColumnsChange}
                                        onResetColumns={resetColumnsToDefault} />
                    <RecentOrdersTable team={team}
                                       confirm={confirm}
                                       ticketTypes={ticketTypes}
                                       addOns={addOns}
                                       paymentMethods={recentOrderPaymentMethods}
                                       seatSections={recentOrderSeatSections}
                                       typedSearchTerm={typedSearchTerm}
                                       searchedSearchTerm={searchedSearchTerm}
                                       startSearchTermUpdate={startSearchTermUpdate}
                                       visibleColumns={visibleColumns}
                                       orderMenuItems={orderMenuItems}
                                       onQueryChange={setCsvQuery} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SlidingPane
        isOpen={Object.keys(order).length > 0}
        className="sliding-pane-container"
        width={"480px"}
        hideHeader={true}
        onAfterOpen={() => {
          resendEmailChanged(order.email || "");
        }}
        onRequestClose={() => {
          orderChanged({});
        }}
      >
        <ResendEmailSlidingState csrfToken={csrfToken}
                                 team={team}
                                 currentUser={currentUser}
                                 confirm={confirm}
                                 order={order}
                                 orderChanged={orderChanged}
                                 sendOrderReceipt={sendOrderReceipt}
                                 resendEmail={resendEmail}
                                 resendEmailChanged={resendEmailChanged}
                                 sendingConfirmationEmail={sendingConfirmationEmail} />
      </SlidingPane>
      <RefundModal csrfToken={csrfToken}
                   team={team}
                   currentUser={currentUser}
                   confirm={confirm}
                   refund={refund}
                   fetchNewRefund={fetchNewRefund}
                   refundReasons={refundReasons}
                   createRefund={createRefund}
                   submittingRefund={submittingRefund}
                   confirmRefund={confirmRefund}
                   venueOwedBalance={venueOwedBalance}
                   canBypassRefundRestrictions={canBypassRefundRestrictions}
                   refundChanged={refundChanged} />
    </div>
  );
}

ConfirmDashboard.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  confirmChanged: PropTypes.func.isRequired,
  ticketTypes: PropTypes.array,
  addOns: PropTypes.array,
  recentOrderPaymentMethods: PropTypes.array,
  recentOrderSeatSections: PropTypes.array,
  typedSearchTerm: PropTypes.string,
  startSearchTermUpdate: PropTypes.func.isRequired,
  searchedSearchTerm: PropTypes.string,
  csvDataSource: PropTypes.string.isRequired,
  order: PropTypes.object,
  orderChanged: PropTypes.func.isRequired,
  sendOrderReceipt: PropTypes.func.isRequired,
  widgetTab: PropTypes.string,
  widgetTabChanged: PropTypes.func.isRequired,
  addOnsByPrice: PropTypes.array,
  refund: PropTypes.object,
  fetchNewRefund: PropTypes.func.isRequired,
  refundChanged: PropTypes.func.isRequired,
  refundReasons: PropTypes.array,
  createRefund: PropTypes.func.isRequired,
  submittingRefund: PropTypes.bool,
  resendEmail: PropTypes.string,
  resendEmailChanged: PropTypes.func.isRequired,
  sendingConfirmationEmail: PropTypes.bool,
  cumulativeSalesData: PropTypes.array,
  fakeSalesData: PropTypes.array,
  fakeSalesDataChanged: PropTypes.func.isRequired,
  confirmRefund: PropTypes.func.isRequired,
  refundOrder: PropTypes.object,
  buyers: PropTypes.array,
  promoters: PropTypes.array,
  ticketTypesByPrice: PropTypes.array,
  venueOwedBalance: PropTypes.number,
  canBypassRefundRestrictions: PropTypes.bool,
  payoutInfo: PropTypes.object,
  salesData: PropTypes.object
};

export default ConfirmDashboard;
